import { useEffect, useState, useCallback, useRef, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2.ts';
import { trackEcommerce, eventNameConstants } from 'dibs-tracking';
import { SiteNavItem } from './SiteNavItem.tsx';
import { FullWidthDropdown } from '../Dropdown/FullWidthDropdown.tsx';
import { SiteNavDropdownContent } from './SiteNavDropdownContent.tsx';
import { SITE_NAV_AB_TEST_NAME } from '../../siteNavAbTestConfig.ts';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SiteNav-style.scss';

import { type SiteNav_viewer$key } from './__generated__/SiteNav_viewer.graphql.ts';

const { EVENT_VIEW_PROMOTION } = eventNameConstants;

type Props = {
    viewer: SiteNav_viewer$key;
};

export const SiteNav: FC<Props> = ({ viewer: viewerRef }) => {
    const viewer = useFragment(
        graphql`
            fragment SiteNav_viewer on Viewer {
                navigation(rootId: $previewKey, variantId: $variantId) {
                    modules {
                        ...SiteNavItem_navigationModule
                        ...SiteNavDropdownContent_navigationModule
                        name
                        imageLinkData {
                            path
                        }
                        items {
                            __typename
                        }
                    }
                }
            }
        `,
        viewerRef
    );
    const { modules } = viewer?.navigation || {};
    const [activeDropdownIndex, setActiveDropdownIndex] = useState<number | null>(null);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [isNavigationHovered, setIsNavigationHovered] = useState(false);
    const impressedPromotionsRef = useRef(new Set<string>());

    useEffect(() => {
        trackAbTestV2Variant(SITE_NAV_AB_TEST_NAME);
    }, []);

    const handleHide = useCallback(() => {
        setActiveDropdownIndex(null);
        setIsDropdownVisible(false);
        setIsNavigationHovered(false);
    }, []);

    const handleShow = useCallback(
        (index: number) => {
            if (activeDropdownIndex !== index) {
                if (activeDropdownIndex !== null) {
                    handleHide();
                }

                const module = modules?.[index];

                if (module?.items?.length) {
                    setIsDropdownVisible(true);
                }

                setActiveDropdownIndex(index);
                setIsNavigationHovered(true);

                const { name, imageLinkData } = module || {};
                const { path } = imageLinkData || {};
                if (name && path && !impressedPromotionsRef.current.has(path)) {
                    trackEcommerce({
                        type: 'promotionImpression',
                        promotions: [
                            {
                                id: name.toLowerCase(),
                                name,
                                creative: path,
                                position: 'global nav',
                            },
                        ],
                        eventName: EVENT_VIEW_PROMOTION,
                    });
                    impressedPromotionsRef.current.add(path);
                }
            }
        },
        [activeDropdownIndex, handleHide, modules]
    );

    return (
        <nav
            className={classnames(
                dibsCss.flex,
                dibsCss.justifyCenter,
                dibsCss.absolute,
                dibsCss.left0,
                dibsCss.right0,
                dibsCss.hFull
            )}
            onMouseLeave={handleHide}
        >
            <ul
                className={classnames(
                    styles.items,
                    dibsCss.flex,
                    dibsCss.m0,
                    dibsCss.p0,
                    dibsCss.gapMedium
                )}
            >
                {(modules || []).map((module, index) => {
                    if (!module) {
                        return null;
                    }

                    return (
                        <SiteNavItem
                            key={`nav-position-${index}`}
                            navigationModule={module}
                            isNavigationHovered={isNavigationHovered}
                            isVisible={isNavigationHovered && activeDropdownIndex === index}
                            onShow={() => handleShow(index)}
                            onHide={handleHide}
                        />
                    );
                })}
            </ul>
            <FullWidthDropdown isVisible={isDropdownVisible}>
                {(modules || []).map((module, index) => {
                    if (!module) {
                        return null;
                    }

                    const isActive = isDropdownVisible && activeDropdownIndex === index;

                    return (
                        <div
                            key={`dropdown-content-${index}`}
                            style={{ display: isActive ? 'block' : 'none' }}
                        >
                            <SiteNavDropdownContent
                                trackingLabelPrefix={module?.name || ''}
                                navigationModule={module}
                                isVisible={isActive}
                            />
                        </div>
                    );
                })}
            </FullWidthDropdown>
        </nav>
    );
};
